import type { MonthEndEntry, MonthEndReport } from '@urbanx/agx.rex.client';
import { toInclusiveDate } from '@urbanx/agx.ui-components2';

export const monthEndFilename = (report: MonthEndReport) =>
  `month-end-${report.fromDate}-to-${toInclusiveDate(report.toDateExclusive)}.csv`;

const encodeCell = (value: string | number) => {
  if (typeof value === 'number') return String(value);
  // Excel also recognizes formula prefixes after whitespace/control characters.
  // eslint-disable-next-line no-control-regex -- spreadsheet formula protection includes control characters
  const literal = /^[\s\u0000-\u001f]*[=+@-]|^[\t\r\n]/.test(value) ? `'${value}` : value;
  return `"${literal.replaceAll('"', '""')}"`;
};

export const monthEndCsv = (report: MonthEndReport) => {
  const rows: (string | number)[][] = [
    [
      'From date',
      'To date (inclusive)',
      'Section',
      'Metric',
      'Basis',
      'Place',
      'Kind',
      'Name/property',
      'Value',
    ],
  ];
  const add = (section: string, metric: string, basis: string, entry: MonthEndEntry) => {
    rows.push([
      report.fromDate,
      toInclusiveDate(report.toDateExclusive),
      section,
      metric,
      basis,
      entry.place,
      entry.kind,
      entry.name,
      entry.value,
    ]);
  };
  const addEmpty = (section: string, metric: string, basis: string) => {
    rows.push([
      report.fromDate,
      toInclusiveDate(report.toDateExclusive),
      section,
      metric,
      basis,
      '',
      '',
      'No qualifying winner',
      '',
    ]);
  };
  for (const total of report.networkTotals) {
    for (const [metric, value] of [
      ['Sales volume', total.salesVolume],
      ['GCI', total.gci],
    ] as const) {
      rows.push([
        report.fromDate,
        toInclusiveDate(report.toDateExclusive),
        'Network',
        metric,
        total.basis,
        '',
        'Network',
        'Network',
        value,
      ]);
    }
  }
  for (const [section, entries] of [
    ['Individual/Team', report.individualTeamGciRankings],
    ['Agency', report.agencyGciRankings],
  ] as const) {
    if (entries.length === 0) {
      addEmpty(`Number one ${section}`, 'GCI', 'Unconditional');
      addEmpty(`Top Ten ${section}`, 'GCI', 'Unconditional');
    }
    for (const entry of entries.filter((entry) => entry.place === 1))
      add(`Number one ${section}`, 'GCI', 'Unconditional', entry);
    for (const entry of entries) add(`Top Ten ${section}`, 'GCI', 'Unconditional', entry);
  }
  for (const award of report.awards) {
    if (award.winners.length === 0) {
      addEmpty(
        award.name,
        award.metric,
        award.metric === 'Listings' ? 'Listing created' : 'Unconditional'
      );
    }
    for (const entry of award.winners)
      add(
        award.name,
        award.metric,
        award.metric === 'Listings' ? 'Listing created' : 'Unconditional',
        entry
      );
  }
  return `\uFEFF${rows.map((row) => row.map(encodeCell).join(',')).join('\r\n')}\r\n`;
};
